import { supabase } from '../lib/supabase';

// Featured Content Service for managing hero section content
export const featuredContentService = {
  // Get featured content (alias for admin access)
  async getFeaturedContent() {
    try {
      const { data, error } = await supabase?.from('featured_content')?.select(`
          id,
          title,
          subtitle,
          image_path,
          display_order,
          is_active,
          created_at,
          updated_at,
          created_by,
          user_profiles:created_by(full_name, email)
        `)?.order('display_order', { ascending: true });

      if (error) throw error;

      // Generate public URLs for images
      const contentWithImages = data?.map(item => ({
        ...item,
        imageUrl: item?.image_path ? 
          supabase?.storage?.from('featured-images')?.getPublicUrl(item?.image_path)?.data?.publicUrl 
          : null
      }));

      return contentWithImages;
    } catch (error) {
      console.error('Error fetching featured content:', error);
      throw error;
    }
  },

  // Get all featured content (public access)
  async getAllFeaturedContent() {
    try {
      const { data, error } = await supabase?.from('featured_content')?.select(`
          id,
          title,
          subtitle,
          image_path,
          display_order,
          is_active,
          created_at,
          updated_at,
          created_by,
          user_profiles:created_by(full_name, email)
        `)?.eq('is_active', true)?.order('display_order', { ascending: true });

      if (error) throw error;

      // Generate public URLs for images
      const contentWithImages = data?.map(item => ({
        ...item,
        imageUrl: item?.image_path ? 
          supabase?.storage?.from('featured-images')?.getPublicUrl(item?.image_path)?.data?.publicUrl 
          : null
      }));

      return { data: contentWithImages, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  },

  // Get featured content for admin (includes inactive)
  async getAdminFeaturedContent() {
    try {
      const { data, error } = await supabase?.from('featured_content')?.select(`
          id,
          title,
          subtitle,
          image_path,
          display_order,
          is_active,
          created_at,
          updated_at,
          created_by,
          user_profiles:created_by(full_name, email)
        `)?.order('display_order', { ascending: true });

      if (error) throw error;

      // Generate public URLs for images
      const contentWithImages = data?.map(item => ({
        ...item,
        imageUrl: item?.image_path ? 
          supabase?.storage?.from('featured-images')?.getPublicUrl(item?.image_path)?.data?.publicUrl 
          : null
      }));

      return { data: contentWithImages, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  },

  // Create new featured content
  async createFeaturedContent(contentData) {
    try {
      const { data, error } = await supabase?.from('featured_content')?.insert([{
          title: contentData?.title,
          subtitle: contentData?.subtitle,
          image_path: contentData?.image_path,
          display_order: contentData?.display_order || 0,
          is_active: contentData?.is_active ?? true,
          created_by: contentData?.created_by
        }])?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  },

  // Update featured content
  async updateFeaturedContent(id, contentData) {
    try {
      const { data, error } = await supabase?.from('featured_content')?.update({
          title: contentData?.title,
          subtitle: contentData?.subtitle,
          image_path: contentData?.image_path,
          display_order: contentData?.display_order,
          is_active: contentData?.is_active
        })?.eq('id', id)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  },

  // Delete featured content
  async deleteFeaturedContent(id) {
    try {
      const { error } = await supabase?.from('featured_content')?.delete()?.eq('id', id);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error?.message };
    }
  },

  // Reorder featured content
  async reorderFeaturedContent(items) {
    try {
      const updates = items?.map((item, index) => ({
        id: item?.id,
        display_order: index + 1
      }));

      const { data, error } = await supabase?.from('featured_content')?.upsert(updates, { onConflict: 'id' })?.select();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  },

  // Upload featured image
  async uploadFeaturedImage(file, filename) {
    try {
      const fileExt = file?.name?.split('.')?.pop();
      const filePath = `${filename}.${fileExt}`;

      const { data, error } = await supabase?.storage?.from('featured-images')?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (error) throw error;

      // Get public URL
      const { data: urlData } = supabase?.storage?.from('featured-images')?.getPublicUrl(filePath);

      return { 
        data: { 
          path: filePath,
          url: urlData?.publicUrl 
        }, 
        error: null 
      };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  },

  // Delete featured image
  async deleteFeaturedImage(imagePath) {
    try {
      const { error } = await supabase?.storage?.from('featured-images')?.remove([imagePath]);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error?.message };
    }
  },

  // Get featured image URL
  getFeaturedImageUrl(imagePath) {
    if (!imagePath) return null;
    return supabase?.storage?.from('featured-images')?.getPublicUrl(imagePath)?.data?.publicUrl;
  }
};

export default featuredContentService;